import { isDictatedReport } from "@rosc/contracts";
import { ListSectionHeader, ListSectionMore } from "@rosc/ui/v32/list-section";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ClipboardList,
  ListFilter,
  PanelLeftClose,
  Plus,
  Search,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { PracticeBadge } from "@/components/ui/guided/PracticeVisit";
import { V3IconButton } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { builtInTemplateLabel } from "@/lib/document-name";
import { useFeature } from "@/lib/early-access";
import { matchPracticeVisit } from "@/lib/guided/practice-visits";
import { isPreChartDraft } from "@/lib/pre-chart";
import { usePreChartSectionCollapsed } from "@/lib/pre-chart-section";
import { cn, getVisitTitle } from "@/lib/utils";
import {
  countByFilter,
  dateKindFor,
  filterVisits,
  formatVisitDuration,
  groupVisits,
  hasReviewCounts,
  PRE_CHART_SECTION_LIMIT,
  parseVisitDate,
  type VisitGroupKey,
  type VisitStatusFilter,
} from "@/pages/Visits/redesign/visit-list-model";
import type { VisitSummary } from "@/services/visits/read-model";
import { useAuthStore } from "@/store/auth.store";
import { useWorkspacePanelStore } from "@/store/workspacePanel.store";
import { AccountMenu } from "./AccountMenu";
import { VisitStatusIcon, visitGlyph } from "./VisitStatusIcon";

export type VisitBrowserMode = "sidebar" | "page";

interface Props {
  visits: VisitSummary[];
  loading?: boolean;
  // "sidebar": the desktop sidebar (iPad). "page": the phone visits screen
  // (iPhone), full width with a floating New visit.
  mode?: VisitBrowserMode;
  // Desktop sidebar: the visits page (its own search and filters) is open,
  // so "All visits" is the current row. The sidebar is the same on every
  // page: logo, search, New visit, Pre-chart, All visits, the visits by day.
  onVisitsPage?: boolean;
  onCollapse?: () => void;
  // Fixed "now" for stories and tests.
  now?: Date;
  // Shown above the list in page mode (load error, onboarding notices).
  notice?: React.ReactNode;
  // Shown instead of the list when there are no visits (page mode).
  empty?: React.ReactNode;
}

const STATUS_FILTERS = ["all", "processing", "ready", "error"] as const;

const capitalize = (value: string): string =>
  value.charAt(0).toLocaleUpperCase() + value.slice(1);

const useGroupLabel = (): ((key: VisitGroupKey) => string) => {
  const { t, i18n } = useTranslation();
  return (key) =>
    key.kind === "month"
      ? capitalize(
          new Date(key.year, key.month, 1).toLocaleDateString(
            i18n.language || "en-US",
            { month: "long", year: "numeric" },
          ),
        )
      : t(`visits.home.groups.${key.kind}`);
};

// "5:47 PM · SOAP · 2:15", with the date instead of the time for older
// visits.
export const visitSubline = (
  visit: VisitSummary,
  group: VisitGroupKey["kind"],
  language: string,
): string => {
  const date = parseVisitDate(visit.createDate);
  const when = !date
    ? null
    : group === "today" || group === "yesterday"
      ? date.toLocaleTimeString(language, {
          hour: "numeric",
          minute: "2-digit",
        })
      : date.toLocaleDateString(language, { month: "short", day: "numeric" });
  return [
    when,
    visit.template ? builtInTemplateLabel(visit.template) : visit.template,
    formatVisitDuration(visit.duration),
  ]
    .filter(Boolean)
    .join(" · ");
};

// Starts a pre-chart from anywhere: the visits page opens the Pre-chart sheet
// for `openPreChart` (VITE_PRE_CHART_ENABLED).
export const usePreChart = (): (() => void) => {
  const navigate = useNavigate();
  return () => {
    void navigate({ to: "/visits", search: { openPreChart: true } as never });
  };
};

// Starts a new visit from anywhere: the visits page opens the New visit
// sheet for `openNewVisit`.
export const useNewVisit = (): (() => void) => {
  const navigate = useNavigate();
  return () => {
    // Back from New visit returns here (v3.2 desktop, WorkspacePanel).
    const from = window.location.pathname;
    useWorkspacePanelStore
      .getState()
      .set({ returnTo: from === "/visits" ? null : from });
    void navigate({ to: "/visits", search: { openNewVisit: true } as never });
  };
};

const isTyping = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

// ⌘N / Ctrl+N where the browser lets the page have it, and N on its own
// outside text fields (as in the v3.2 prototype).
export const useNewVisitShortcut = (
  start: () => void,
  enabled: boolean,
): void => {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== "n" || event.altKey || event.shiftKey)
        return;
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier && isTyping(event.target)) return;
      if (!modifier && document.querySelector('[role="dialog"]')) return;
      event.preventDefault();
      start();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [start, enabled]);
};

const VisitRow = ({
  visit,
  group,
  selected,
  mode,
}: {
  visit: VisitSummary;
  group: VisitGroupKey["kind"];
  selected: boolean;
  mode: VisitBrowserMode;
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const title = getVisitTitle(visit.title, visit.rowno);
  const glyph = visitGlyph(visit);
  const sub = visitSubline(visit, group, i18n.language || "en-US");
  const page = mode === "page";
  // Guided first visit: a practice consulta says so.
  const account = useAuthStore((state) => state.userData?.public_id);
  const guided = useFeature("guided-first-visit");
  const practice =
    guided &&
    matchPracticeVisit(account, {
      public_id: String(visit.public_id),
      title: visit.title,
    });
  return (
    <li>
      <Link
        aria-current={selected ? "page" : undefined}
        className={cn(
          "group flex items-center gap-3 font-v3 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-v3-brand",
          page
            ? "min-h-[64px] px-4 py-2.5 hover:bg-v3-surface-2"
            : "min-h-[52px] rounded-[10px] px-2.5 py-1.5 hover:bg-[rgba(127,127,140,0.08)]",
          selected &&
            !page &&
            "bg-v3-panel shadow-v3-hairline hover:bg-v3-panel",
        )}
        data-testid="sidebar-visit"
        params={{ visitPublicId: String(visit.public_id) }}
        to="/visits/$visitPublicId"
      >
        <VisitStatusIcon glyph={glyph} />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate font-medium text-v3-text",
              page ? "text-[16px] font-semibold" : "text-[13.5px]",
            )}
          >
            {title}
          </span>
          {sub && (
            <span
              className={cn(
                "block truncate tabular-nums text-v3-text-3",
                page ? "text-[13.5px]" : "text-[12px]",
              )}
            >
              {sub}
            </span>
          )}
        </span>
        {practice && glyph !== "writing" && (
          <PracticeBadge className="shrink-0" />
        )}
        {glyph === "writing" && (
          <span className="shrink-0 rounded-full bg-v3-brand-tint px-2 py-0.5 text-[11.5px] font-medium text-v3-brand-strong">
            {t("v3.shell.writingNote")}
          </span>
        )}
        {glyph === "error" && (
          <span className="shrink-0 rounded-full bg-v3-rec-tint px-2 py-0.5 text-[11.5px] font-medium text-v3-rec-text">
            {t("visit.statusFile.error")}
          </span>
        )}
        {isDictatedReport(visit.status) && (
          <span
            className="shrink-0 rounded-full bg-v3-brand-tint px-2 py-px text-[11.5px] font-medium text-v3-brand-strong"
            data-testid="sidebar-visit-report"
          >
            {t("dictationReport.badge")}
          </span>
        )}
        {isPreChartDraft(visit.status) && (
          <span
            className="shrink-0 rounded-full border border-dashed border-v3-border-strong px-2 py-px text-[11.5px] font-medium text-v3-text-2"
            data-testid="sidebar-visit-pre-chart"
          >
            {t("preChart.badgeShort")}
          </span>
        )}
      </Link>
    </li>
  );
};

// The visits list of the v3.2 shell: on desktop the sidebar (logo, search
// button, New visit, Pre-chart, All visits, visits by day, account), on phones the visits
// screen that pushes to a visit.
export const VisitBrowser = ({
  visits,
  loading = false,
  mode = "sidebar",
  onVisitsPage = false,
  onCollapse,
  now,
  notice,
  empty,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { visitPublicId?: string };
  const selectedId = params.visitPublicId?.toLowerCase();
  const groupLabel = useGroupLabel();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<VisitStatusFilter>("all");
  const startNewVisit = useNewVisit();
  useNewVisitShortcut(startNewVisit, mode === "sidebar");
  const startPreChart = usePreChart();
  const preChart = AppConfig.enablePreChart;
  const counts = useMemo(() => countByFilter(visits), [visits]);
  const showReview =
    hasReviewCounts(visits) &&
    (counts.needsReview > 0 || filter === "needsReview");
  const shown = useMemo(
    () => filterVisits(visits, query, filter),
    [visits, query, filter],
  );
  const page = mode === "page";
  // The sidebar is the same on every page: no status filters (the visits
  // page has them). Its search button opens a field in place, above the
  // list, so searching from inside a visit doesn't leave the visit.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchId = useId();
  const sidebarShown = useMemo(
    () => (searchOpen && query ? filterVisits(visits, query, "all") : visits),
    [visits, query, searchOpen],
  );
  const listed = page ? shown : sidebarShown;
  // Pre-chart drafts pinned in their own foldable section on top.
  const groups = useMemo(
    () => groupVisits(listed, now, { pinPreChart: preChart }),
    [listed, now],
  );
  const [preChartCollapsed, togglePreChart] = usePreChartSectionCollapsed();
  const listIdPrefix = useId();
  const [allDrafts, setAllDrafts] = useState(false);
  const closeSearch = (): void => {
    setSearchOpen(false);
    setQuery("");
  };
  const panel = useWorkspacePanelStore();
  const filters = STATUS_FILTERS.filter(
    (value) =>
      value === "all" ||
      counts[value] > 0 ||
      filter === value ||
      value === "ready",
  );

  const search = (
    <div className="relative">
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-v3-text-3"
      />
      <input
        // biome-ignore lint/a11y/noAutofocus: opened on request from the sidebar's search button
        autoFocus={!page}
        aria-label={t("visits.home.search")}
        className={cn(
          "w-full rounded-full border-0 bg-v3-surface-3/70 pl-9 pr-10 [&::-webkit-search-cancel-button]:appearance-none font-v3 text-v3-text outline-hidden placeholder:text-v3-text-3 focus:ring-2 focus:ring-v3-brand-ring dark:bg-v3-surface-2",
          page
            ? "h-11 text-[16px]"
            : "h-11 text-[16px] lg:h-9 lg:text-[13.5px]",
        )}
        data-testid="sidebar-search"
        id={page ? undefined : searchId}
        placeholder={t("v3.shell.searchVisits")}
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (page || event.key !== "Escape") return;
          event.preventDefault();
          closeSearch();
        }}
      />
      {query && (
        <V3IconButton
          className="absolute right-0 top-1/2 -translate-y-1/2"
          label={t("visits.home.clearSearch")}
          onClick={() => setQuery("")}
        >
          <X />
        </V3IconButton>
      )}
    </div>
  );

  const chips = (
    <fieldset
      className={cn(
        "flex gap-1.5",
        // Phone page: one row that scrolls; the edges fade inside the
        // gutter, so a chip running past the screen reads as "more", not cut.
        page
          ? "-mx-4 overflow-x-auto px-4 pb-1 scrollbar-none [mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%-40px),transparent)] [&>button]:shrink-0"
          : "flex-wrap",
      )}
    >
      <legend className="sr-only">{t("visits.home.filterLabel")}</legend>
      {filters.map((value) => (
        <V3Chip
          key={value}
          aria-pressed={filter === value}
          data-testid={`sidebar-filter-${value}`}
          size={page ? "default" : "sm"}
          onClick={() => setFilter(value)}
        >
          {value === "error" && (
            <span aria-hidden className="size-1.5 rounded-full bg-v3-rec" />
          )}
          {t(`visits.home.filters.${value}`)}
          {value !== "all" && (
            <span className="opacity-70">{counts[value]}</span>
          )}
        </V3Chip>
      ))}
      {showReview && (
        <V3Chip
          aria-pressed={filter === "needsReview"}
          data-testid="sidebar-filter-needsReview"
          size={page ? "default" : "sm"}
          title={t("visits.home.needsReviewHint")}
          variant="warn"
          onClick={() =>
            setFilter(filter === "needsReview" ? "all" : "needsReview")
          }
        >
          {t("visits.home.filters.needsReview")}
          <span className="opacity-80">{counts.needsReview}</span>
        </V3Chip>
      )}
    </fieldset>
  );

  // v3.2 desktop: New visit open in the workspace column.
  const newVisitRow = !page && panel.active === "new-visit" && (
    <ul aria-label={t("newVisitV32.title")} className="pt-2">
      <li>
        <p
          aria-current="page"
          className="flex min-h-[52px] items-center gap-3 rounded-[10px] bg-v3-panel px-2.5 py-1.5 font-v3 shadow-v3-hairline"
          data-testid="sidebar-new-visit-row"
        >
          <span
            aria-hidden
            className={cn(
              "size-2.5 shrink-0 rounded-full",
              panel.recording ? "animate-pulse bg-v3-rec" : "bg-v3-text-3",
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-medium text-v3-text">
              {t("newVisitV32.title")}
            </span>
            <span className="block truncate text-[12px] text-v3-text-3">
              {panel.recording
                ? t("recorderV32.recording")
                : t("v3.shell.status.none")}
            </span>
          </span>
        </p>
      </li>
    </ul>
  );

  const list =
    loading && visits.length === 0 ? (
      <div aria-busy className="space-y-2 px-2 pt-2" role="status">
        <span className="sr-only">{t("visits.home.loading")}</span>
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={index}
            aria-hidden
            className="h-10 animate-pulse rounded-lg bg-v3-surface-3/60"
          />
        ))}
      </div>
    ) : groups.length === 0 ? (
      <p className="px-3 py-6 text-center text-[13px] text-v3-text-3">
        {visits.length === 0
          ? t("v3.shell.noVisits")
          : t("visits.home.noResults.title")}
      </p>
    ) : (
      groups.map((group) => {
        const label = groupLabel(group.key);
        const pinned = group.key.kind === "preChart";
        const listId = `${listIdPrefix}-${group.id}`;
        const folded = pinned && preChartCollapsed;
        const trimmed = pinned && group.visits.length > PRE_CHART_SECTION_LIMIT;
        const rows =
          trimmed && !allDrafts
            ? group.visits.slice(0, PRE_CHART_SECTION_LIMIT)
            : group.visits;
        return (
          <section
            key={group.id}
            aria-label={label}
            data-testid={pinned ? "visits-pre-chart-section" : undefined}
          >
            {pinned ? (
              <ListSectionHeader
                controls={listId}
                count={group.visits.length}
                expanded={!folded}
                icon={<ClipboardList />}
                size={page ? "page" : "sidebar"}
                testId="visits-pre-chart-toggle"
                title={label}
                onToggle={togglePreChart}
              />
            ) : (
              <h2
                className={cn(
                  "font-v3 font-medium text-v3-text-3",
                  page
                    ? "px-1 pb-2 pt-5 text-[13.5px]"
                    : "px-2.5 pb-1 pt-4 text-[12px]",
                )}
              >
                {label}
              </h2>
            )}
            {!folded && (
              <ul
                aria-label={t("visits.home.listLabel", { group: label })}
                className={cn(
                  page
                    ? "divide-y divide-v3-border overflow-hidden rounded-[14px] bg-v3-panel shadow-v3-hairline"
                    : "space-y-0.5",
                )}
                id={listId}
              >
                {rows.map((visit) => (
                  <VisitRow
                    key={visit.public_id}
                    group={dateKindFor(visit, group.key.kind, now)}
                    mode={mode}
                    selected={
                      String(visit.public_id).toLowerCase() === selectedId
                    }
                    visit={visit}
                  />
                ))}
                {trimmed && (
                  <ListSectionMore
                    controls={listId}
                    expanded={allDrafts}
                    label={
                      allDrafts
                        ? t("visits.home.preChartShowLess")
                        : t("visits.home.preChartShowAll", {
                            count: group.visits.length,
                          })
                    }
                    testId="visits-pre-chart-more"
                    variant={page ? "card" : "sidebar"}
                    onToggle={() => setAllDrafts((value) => !value)}
                  />
                )}
              </ul>
            )}
          </section>
        );
      })
    );

  if (page) {
    return (
      <div
        className="relative min-h-full bg-v3-canvas px-4 pb-28 font-v3 text-v3-text"
        data-testid="visit-browser-page"
      >
        <div className="flex h-14 items-center gap-2">
          <img
            alt=""
            className="size-7"
            src={`${import.meta.env.BASE_URL}images/logos/logo.svg`}
          />
          <span className="flex-1 text-[17px] font-semibold tracking-tight">
            ROSC
          </span>
          <AccountMenu align="end" />
        </div>
        {search}
        <h1 className="mt-5 font-v3-display text-[32px] font-bold leading-tight tracking-tight">
          {t("visits.title")}
        </h1>
        <div className="mt-3">{chips}</div>
        {notice && <div className="mt-3">{notice}</div>}
        {!loading && visits.length === 0 && empty ? (
          <div className="mt-4">{empty}</div>
        ) : (
          list
        )}
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center gap-2 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {preChart && (
            <button
              className="pointer-events-auto inline-flex h-14 items-center gap-2 rounded-full bg-v3-panel px-5 text-[16px] font-semibold text-v3-text shadow-v3-float active:scale-[0.97]"
              data-testid="pre-chart-fab"
              type="button"
              onClick={startPreChart}
            >
              <ClipboardList aria-hidden className="size-5" />
              {t("preChart.button")}
            </button>
          )}
          <button
            className="pointer-events-auto inline-flex h-14 items-center gap-2 rounded-full bg-v3-ink px-6 text-[16px] font-semibold text-v3-on-ink shadow-v3-float active:scale-[0.97]"
            data-testid="new-visit-fab"
            type="button"
            onClick={startNewVisit}
          >
            <Plus aria-hidden className="size-5" />
            {t("v3.shell.newVisit")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <nav
      aria-label={t("v3.shell.navigation")}
      className="flex h-full min-h-0 flex-col gap-2 pb-2 pl-3 pr-2 pt-2 font-v3 text-v3-text"
      data-testid="v3-sidebar"
    >
      <div className="flex h-11 items-center gap-1.5 px-1">
        <img
          alt=""
          className="size-6"
          src={`${import.meta.env.BASE_URL}images/logos/logo.svg`}
        />
        <span className="flex-1 font-v3-display text-[15.5px] font-semibold tracking-tight">
          ROSC
        </span>
        <V3IconButton
          aria-controls={searchOpen ? searchId : undefined}
          aria-expanded={searchOpen}
          className={cn(searchOpen && "bg-v3-surface-3 text-v3-text")}
          data-testid="sidebar-search-button"
          label={t("v3.shell.searchVisits")}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
        >
          <Search />
        </V3IconButton>
        {onCollapse && (
          <V3IconButton
            data-testid="sidebar-collapse"
            label={t("v3.shell.hideSidebar")}
            onClick={onCollapse}
          >
            <PanelLeftClose />
          </V3IconButton>
        )}
      </div>
      {searchOpen && <div className="px-0.5">{search}</div>}
      <button
        aria-keyshortcuts="Meta+N Control+N N"
        className="flex h-11 items-center gap-2 rounded-[10px] bg-v3-panel px-3 text-[13.5px] font-semibold shadow-[0_0_0_1px_var(--v3-border),0_1px_2px_rgba(0,0,0,0.04)] transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-v3-brand lg:h-9"
        data-testid="sidebar-new-visit"
        type="button"
        onClick={startNewVisit}
      >
        <Plus aria-hidden className="size-4 text-v3-text-2" />
        <span className="flex-1 text-left">{t("v3.shell.newVisit")}</span>
        <kbd className="rounded-[5px] px-1.5 text-[11px] font-normal leading-4 text-v3-text-3 shadow-v3-hairline-strong">
          ⌘N
        </kbd>
      </button>
      {preChart && (
        <button
          className="-mt-1 flex h-11 items-center gap-2 rounded-[10px] px-3 text-[13.5px] font-medium text-v3-text-2 transition-colors hover:bg-[rgba(127,127,140,0.08)] hover:text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand lg:h-9"
          data-testid="sidebar-pre-chart"
          type="button"
          onClick={startPreChart}
        >
          <ClipboardList aria-hidden className="size-4" />
          <span className="flex-1 text-left">{t("preChart.button")}</span>
        </button>
      )}
      <Link
        aria-current={onVisitsPage ? "page" : undefined}
        className={cn(
          "-mt-1 flex h-11 shrink-0 items-center gap-2 rounded-[10px] px-3 text-[13.5px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-v3-brand lg:h-9",
          onVisitsPage
            ? "bg-v3-panel text-v3-text shadow-v3-hairline"
            : "text-v3-text-2 hover:bg-[rgba(127,127,140,0.08)] hover:text-v3-text",
        )}
        data-testid="sidebar-all-visits"
        to="/visits"
      >
        <ListFilter aria-hidden className="size-4" />
        <span className="flex-1 text-left">{t("v3.shell.allVisits")}</span>
        {counts.error > 0 ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-v3-rec-tint px-1.5 text-[11.5px] font-semibold tabular-nums text-v3-rec-text"
            title={t("visits.home.filters.error")}
          >
            <span aria-hidden className="size-1.5 rounded-full bg-v3-rec" />
            {counts.error}
          </span>
        ) : (
          <span className="text-[12px] tabular-nums text-v3-text-3">
            {visits.length || ""}
          </span>
        )}
      </Link>
      <div className="-mr-2 min-h-0 flex-1 overflow-y-auto pr-2">
        {newVisitRow}
        {list}
      </div>
      {/* The account (iPad: bottom of the sidebar); the menu opens upward. */}
      <div className="mt-auto shrink-0 border-t border-v3-border pt-2">
        <AccountMenu variant="row" />
      </div>
    </nav>
  );
};
